import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Trophy, Bot, User } from "lucide-react";
import { api, formatXAF } from "@/lib/api";
import { Die } from "@/components/Dice";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { playDice, playWin } from "@/lib/sound";
import { SoundToggle } from "@/components/SoundToggle";

const GUEST_KEY = "bkm_guest_demo_used";

const flatten = (g) => g.rounds.flatMap((r) => r.results.map((x) => ({ ...x, round: r.round })));

const PlayerRow = ({ g, pid, roll, active, winner }) => (
  <div className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${active ? "border-amber-500/60 bg-amber-500/10" : winner ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`}
    data-testid={`demo-player-${pid === g.me ? "me" : pid.slice(0, 4)}`}>
    {pid === g.me ? <User className="h-5 w-5 text-amber-400" /> : <Bot className="h-5 w-5 text-slate-400" />}
    <span className="min-w-0 flex-1 truncate font-semibold">{g.names[pid]}</span>
    {roll ? (
      <div className="flex items-center gap-2">
        {roll.dice.map((v, i) => <Die key={i} value={v} size={34} flat ivory={pid === g.me} />)}
        <span className="w-8 text-right font-display text-lg font-bold">{roll.total}</span>
      </div>
    ) : <span className="text-xs text-slate-500">—</span>}
    {winner && <Trophy className="h-5 w-5 text-emerald-400" />}
  </div>
);

const Intro = ({ onStart, busy }) => {
  const { t } = useI18n();
  return (
    <div className="surface space-y-4 p-6" data-testid="demo-intro">
      <Gift className="h-8 w-8 text-amber-400" />
      <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("demo.title")}</h1>
      <p className="text-sm text-slate-300">{t("demo.text")}</p>
      <p className="text-xs text-slate-500">{t("demo.noMoney")}</p>
      <button type="button" onClick={onStart} disabled={busy} data-testid="demo-start-btn"
        className="inline-flex h-12 items-center justify-center rounded-full bg-amber-500 px-8 font-display font-bold text-[#0B0D12] transition-transform hover:bg-amber-400 active:scale-95 disabled:opacity-60">
        {t("demo.start")}
      </button>
    </div>
  );
};

const Used = ({ guest }) => {
  const { t } = useI18n();
  return (
    <div className="surface space-y-4 p-6" data-testid="demo-used">
      <p className="text-sm text-slate-300">{t(guest ? "demo.guestUsed" : "demo.used")}</p>
      <Link to={guest ? "/auth?mode=signup" : "/lobby"} className="inline-flex h-12 items-center rounded-full bg-amber-500 px-8 font-bold text-[#0B0D12]" data-testid="demo-used-lobby-link">
        {t(guest ? "demo.signup" : "demo.realGame")}
      </Link>
    </div>
  );
};

const Table = ({ g, guest }) => {
  const { t } = useI18n();
  const seq = useMemo(() => flatten(g), [g]);
  const [step, setStep] = useState(0);
  const done = step >= seq.length;
  const next = seq[step];
  const myTurn = !done && next.player_id === g.me;
  const round = done ? g.rounds[g.rounds.length - 1].round : next.round;
  const shown = seq.slice(0, step).filter((x) => x.round === round);
  const contenders = g.rounds.find((r) => r.round === round).results.map((x) => x.player_id);
  const won = g.winners.includes(g.me);

  useEffect(() => {
    if (step > 0 && step <= seq.length) playDice();
    if (step === seq.length && won) setTimeout(playWin, 700);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (done || myTurn) { return undefined; }
    const id = setTimeout(() => setStep((s) => s + 1), 1100);
    return () => clearTimeout(id);
  }, [step, done, myTurn]);

  return (
    <div className="space-y-4" data-testid="demo-table">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-extrabold">{t("demo.tableTitle")}</h1>
        <SoundToggle />
        <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300" data-testid="demo-pot">
          {t("demo.virtualPot")} {formatXAF(g.pot)}
        </span>
      </div>
      {round > 0 && <p className="text-sm text-amber-300" data-testid="demo-tie-notice">{t("demo.tie")} ({round}/3)</p>}
      <div className="space-y-2">
        {contenders.map((pid) => (
          <PlayerRow key={pid} g={g} pid={pid} roll={shown.find((x) => x.player_id === pid)}
            active={!done && next.player_id === pid} winner={done && g.winners.includes(pid)} />
        ))}
      </div>
      {myTurn && (
        <button type="button" onClick={() => setStep((s) => s + 1)} data-testid="demo-roll-btn"
          className="pulse-ring h-14 w-full rounded-full bg-amber-500 font-display text-lg font-bold text-[#0B0D12] active:scale-95">
          {t("demo.roll")}
        </button>
      )}
      {!done && !myTurn && <p className="text-sm text-slate-400" data-testid="demo-waiting">{g.names[next.player_id]} {t("demo.rolling")}</p>}
      {done && (
        <div className="surface space-y-3 p-5" data-testid="demo-result">
          <p className="font-display text-xl font-bold" data-testid="demo-result-text">
            {won ? `${t("demo.won")} ${formatXAF(g.share)} ${t("demo.virtual")}` : t("demo.lost")}
          </p>
          <p className="text-sm text-slate-400">{t("demo.after")}</p>
          {guest ? (
            <Link to="/auth?mode=signup" className="inline-flex h-12 w-full items-center justify-center rounded-full bg-amber-500 px-6 font-bold text-[#0B0D12]" data-testid="demo-signup-link">{t("demo.signup")}</Link>
          ) : (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Link to="/wallet" className="inline-flex h-12 items-center justify-center rounded-full bg-amber-500 px-6 font-bold text-[#0B0D12]" data-testid="demo-topup-link">{t("demo.topup")}</Link>
            <Link to="/lobby" className="inline-flex h-12 items-center justify-center rounded-full border border-white/15 px-6 font-semibold" data-testid="demo-lobby-link">{t("demo.realGame")}</Link>
          </div>
          )}
          <p className="break-all text-[11px] text-slate-600" data-testid="demo-seed">{t("demo.seed")} {g.seed}</p>
        </div>
      )}
    </div>
  );
};

export default function DemoPage() {
  const { user, loading } = useAuth();
  const guest = !user;
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    if (loading) { return; }
    if (guest) { setState({ used: !!localStorage.getItem(GUEST_KEY) }); return; }
    api.get("/demo/status").then(({ data }) => setState({ used: data.used })).catch(() => setState({ used: true }));
  }, [loading, guest]);
  const start = async () => {
    setState({ busy: true });
    try {
      const { data } = await api.post(guest ? "/demo/guest-play" : "/demo/play");
      if (guest) { localStorage.setItem(GUEST_KEY, "1"); }
      setState({ game: data });
    } catch {
      setState({ used: true });
    }
  };
  if (state.loading) return null;
  return (
    <div className="mx-auto max-w-xl rise" data-testid="demo-page">
      {state.game ? <Table g={state.game} guest={guest} /> : state.used ? <Used guest={guest} /> : <Intro onStart={start} busy={state.busy} />}
    </div>
  );
}
